/* eslint-disable no-restricted-syntax */
export const PrimaryColor = '#2584f5';
export const White = '#FFFFFF';
export const LightestGray = '#F5F5F5';
export const LightGray = '#D3D3D3';
export const Gray = '#818589';
export const DarkGray = '#43434A';
export const Green = '#5ec34e';
export const Red = '#ff0000';
export const SecondaryColor = '#8D9DBE';
export const PrimaryBgColor = '#5794f6';

// Elements colors.
//
// Dark palette rework (#2035 — Arbor feedback that dark mode "doesn't work"):
//   1. Layer hierarchy: previously baseLayer==secondLayer and
//      firstLayer==thirdLayer==appSoft, so cards/panels visually merged with
//      the page bg. New scale gives each layer a distinct elevation step.
//   2. Text softening: normalText dropped from #c9d1d9 (very near-white,
//      ~13:1 contrast, harsh) to #adbac7 (~7.5:1, still WCAG AA at all
//      sizes, feels calmer). "Less white" per customer feedback.
//   3. Borders: hairline bumped from #30363d to #373e47 so card edges are
//      visible against elevated surfaces without being loud.
export const colors = {
  border: {
    dark: '#373e47', // Elevated enough to separate cards/rows on dark surfaces
    light: '#d4d4d8' // zinc-300 (slightly darker for better visibility)
  },
  borderPrimary: {
    dark: '#60a5fa',
    light: '#729ef5'
  },
  inputBorder: {
    dark: '#373e47', // Match border for consistent input framing
    light: '#e4e4e7' // zinc-200 style (subtle, not dark)
  },
  icon: {
    dark: '#adbac7', // Match normalText softness so icons don't scream white
    light: 'rgba(62,72,83,0.87)'
  },
  activeText: {
    dark: '#58a6ff', // Blue accent — WCAG AA against all layers
    // Darkened from #2584f5 (which failed 4.5:1 on light bgs — 3.9:1) to
    // #1a68d0 for WCAG AA on white/light layers. Preserves the blue brand feel.
    light: '#1a68d0'
  },
  inactiveText: {
    dark: '#768390', // Softer muted text; ~4.7:1 vs baseLayer (AA large text)
    light: '#595960'
  },
  normalText: {
    dark: '#adbac7', // Softer than pure white; ~7.5:1 vs baseLayer (AA all sizes)
    light: '#28313e'
  },
  dropdownText: {
    dark: '#58a6ff',
    light: '#28313e'
  },
  // Layer system — progressive elevation. Dark: 0d → 16 → 1c → 22.
  // Light: slate-100 → slate-50 → tuned near-white → white — cool grays with a
  // whisper of blue undertone that pairs with the #1a68d0 brand color.
  // Previous (zinc) values kept in comments for one-line revert if needed.
  baseLayer: {
    dark: '#0d1117', // Page background (deepest)
    light: '#e8edf3' // darkest layer, steps up evenly to white
  },
  firstLayer: {
    dark: '#161b22', // Subtle elevation (table row hover, muted panels)
    light: '#f0f3f7'
  },
  secondLayer: {
    dark: '#1c222b', // Cards, panels — visibly above page bg
    light: '#f7f9fb'
  },
  thirdLayer: {
    dark: '#22272e', // Nested/most elevated (modal content, popovers)
    light: '#ffffff'
  },
  lightestLayer: {
    dark: '#6e7fb3',
    light: '#fafafa'
  },
  datagridBg: {
    dark: 'ag-theme-quartz-auto-dark',
    light: 'ag-theme-quartz'
  },
  // Design system surface tokens — mirror the CSS vars in index.css
  appBackground: {
    dark: '#0d1117',
    light: '#e8edf3' // mirrors baseLayer
  },
  appSoft: {
    dark: '#1c222b', // Matches secondLayer so `bg-app-soft` cards elevate cleanly
    light: '#f7f9fb' // mirrors secondLayer
  },
  hairline: {
    dark: '#373e47', // Match border for consistent divider weight
    light: '#d4d4d8'
  },
  // Sidebar — elevated surface, brighter than the page bg
  sidebar: {
    dark: '#161b22', // Between base and secondLayer — reads as its own surface
    light: '#ffffff'
  },
  // Brand / semantic colors (§2.2 — #2044)
  brand: {
    dark: '#58a6ff',
    light: '#2584f5'
  },
  brand2: {
    dark: '#60bbff',
    light: '#2da6fa'
  },
  brandSoft: {
    dark: '#1a2a3a',
    light: '#e2f0ff'
  },
  accentForeground: {
    dark: '#80b8ff',
    light: '#002c61'
  },
  borderSoft: {
    dark: '#30363d',
    light: '#e0e5eb'
  },
  borderStrong: {
    dark: '#444c56',
    light: '#c4cfdb'
  },
  success: {
    dark: '#3fcf8e',
    // Darkened from #00bb7f (2.4:1 vs white — fail) to #008236 (4.6:1) for
    // WCAG AA when used as button/pill background with white text.
    light: '#008236'
  },
  successSoft: {
    dark: '#0d2b1e',
    light: '#e6f9f3'
  },
  danger: {
    dark: '#f87171',
    // Darkened from #ee343b (4.04:1 vs white — fail) to #c81e26 (5.4:1) for
    // WCAG AA when used as button/pill background with white text.
    light: '#c81e26'
  },
  dangerSoft: {
    dark: '#2b1010',
    light: '#fef2f2'
  },
  inverseText: {
    dark: '#0c121a',
    light: '#ffffff'
  },
  folderIcon: {
    dark: '#fbbf24', // amber-300 — distinctly golden-yellow in dark mode, not orange
    light: '#b45309' // amber-700 — warm gold on light backgrounds
  }
};

export type ShadowToken = 'card' | 'card-hover' | 'pop' | 'soft';

// Shadow tokens — mirror the CSS vars in index.css
export const shadows: Record<ShadowToken, { light: string; dark: string }> = {
  card: {
    light: '0 2px 6px 0 rgb(0 0 0 / 0.10), 0 1px 3px -1px rgb(0 0 0 / 0.08)',
    dark: '0 2px 6px 0 rgb(0 0 0 / 0.38), 0 1px 3px -1px rgb(0 0 0 / 0.28)'
  },
  'card-hover': {
    light: '0 8px 20px 0 rgb(0 0 0 / 0.16), 0 3px 6px -1px rgb(0 0 0 / 0.10)',
    dark: '0 8px 20px 0 rgb(0 0 0 / 0.50), 0 3px 6px -1px rgb(0 0 0 / 0.36)'
  },
  pop: {
    light: '0 16px 36px 0 rgb(0 0 0 / 0.20), 0 6px 12px -2px rgb(0 0 0 / 0.12)',
    dark: '0 16px 36px 0 rgb(0 0 0 / 0.60), 0 6px 12px -2px rgb(0 0 0 / 0.42)'
  },
  soft: {
    light: '0 3px 10px 0 rgb(0 0 0 / 0.08)',
    dark: '0 3px 10px 0 rgb(0 0 0 / 0.28)'
  }
};
